import { CommonModule } from '@angular/common';
import {
    Component,
    InputSignal,
    OnDestroy,
    Signal,
    WritableSignal,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
    untracked,
} from '@angular/core';

import {
    RecentSearchEmptyMessage,
    RecentSearchEmptyTitle,
    Search,
} from '../models';

import { MatIconModule } from '@angular/material/icon';
import {
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
} from '@dat/iq-event-bus';
import { sortBy, isEqual, isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { z } from 'zod';

const SearchPayloadSchema = z.object({
    searches: z.array(
        z.object({
            query: z.object({
                equipmentType: z.string(),
                origin: z.string(),
                destination: z.string(),
            }),
            date: z.string(),
            searchedInMs: z.number(),
        }),
    ),
    hasSearches: z.boolean().optional().nullable(),
    loaded: z.boolean().optional().nullable(),
});

type SearchPayload = z.infer<typeof SearchPayloadSchema>;

@Component({
    selector: 'iq-table-rate-recent-search',
    standalone: true,
    imports: [CommonModule, MatIconModule],
    templateUrl: './iq-table-rate-recent-search.component.html',
    styleUrl: './iq-table-rate-recent-search.component.scss',
})
export class IqTableRateRecentSearchComponent implements OnDestroy {
    id: InputSignal<string> = input('');
    testId: InputSignal<string> = input('');
    currentTestId: Signal<string> = computed(
        () => {
            return !!this.testId() ? this.testId() : this.id();
        },
        {
            equal: isEqual,
        },
    );
    clicked = output<Search>();

    private _eventBus: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    searches: Signal<Search[]> = input([]);

    title: string = RecentSearchEmptyTitle;
    message: string = RecentSearchEmptyMessage;

    uniqueSearches: Signal<Search[]> = computed(
        () => {
            const searches = this.searchesPayload().searches;
            const sorted = sortBy(searches, 'searchedInMs').reverse();

            return sorted
                .map(
                    (search): Search => ({
                        origin: search.query.origin,
                        destination: search.query.destination,
                        equipment:
                            search.query.equipmentType?.[0].toUpperCase() +
                            search.query.equipmentType?.slice(1).toLowerCase(),
                        date: search.date,
                        timestamp: DateTime.fromMillis(
                            search.searchedInMs,
                        ).toISO(),
                        query: search.query,
                    }),
                )
                .slice(0, 10);
        },
        {
            equal: isEqual,
        },
    );

    private _bus: IqEventBusBrokerService = inject(IqEventBusBrokerService);
    private _deregister: Record<string, IqEventBusBrokerHandler> = {};
    loaded: WritableSignal<boolean> = signal(false);

    searchesPayload: WritableSignal<SearchPayload> = signal({ searches: [] });

    constructor() {
        this._deregister = this._bus.registerMap({
            'type:rate-lane-searches:update': {
                callback: (payload: SearchPayload) => {
                    const test: any = SearchPayloadSchema.safeParse(payload);

                    if (!test.success) {
                        console.warn('SearchPayloadSchema Invalid Payload');
                        console.log(test?.error?.issues);
                        return;
                    }

                    this.searchesPayload.set(payload);

                    setTimeout(() => {
                        this.loaded.set(payload.loaded);
                    });
                },
            },
        });

        this._bus.dispatch({
            name: 'type:rate-lane-searches:fetch',
        });
        effect(() => {
            /**
             * This dispatches an event when the searches are loaded
             *
             *
             * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
             *
             * ```json
             * {
             *  "onLoad": [
             *    {
             *      "name": "EVENT_NAME",
             *      "payload": {},
             *      "sourceEventRule": {
             *          "payload": [
             *            {
             *                "path": "id",
             *                "operator": "eq",
             *                "value": "COMPONENT_ID"
             *            },
             *            {
             *                "path": "componentName",
             *                "operator": "eq",
             *                "value": "IqTableRateRecentSearch"
             *            },
             *          ]
             *      },
             *      "rules": []
             *    },
             *   ],
             * }
             * ```
             */
            if (this.loaded()) {
                const dispatch = () =>
                    this._eventBus.dispatch({
                        name: 'type:load',
                        payload: {
                            id: this.id(),
                            componentName: 'IqTableRateRecentSearch',
                            hasNoRecentSearches:
                                this.uniqueSearches().length === 0,
                            searches: this.uniqueSearches(),
                        },
                    });

                untracked(() => {
                    dispatch();
                });
            }
        });
    }

    click(search: Search) {
        /**
         * This dispatches an event when the component clicks on a search
         *
         *
         * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
         *
         * ```json
         * {
         *  "onClick": [
         *    {
         *      "name": "EVENT_NAME",
         *      "payload": {},
         *      "sourceEventRule": {
         *          "payload": [
         *            {
         *                "path": "id",
         *                "operator": "eq",
         *                "value": "COMPONENT_ID"
         *            },
         *            {
         *                "path": "componentName",
         *                "operator": "eq",
         *                "value": "IqTableRateRecentSearch"
         *            },
         *          ]
         *      },
         *      "rules": []
         *    },
         *   ],
         * }
         * ```
         */
        const dispatch = () =>
            this._eventBus.dispatch({
                name: 'type:click',
                payload: {
                    id: this.id(),
                    componentName: 'IqTableRateRecentSearch',
                    ...search,
                    query: search.query,
                },
            });

        dispatch();
    }

    ngOnDestroy() {
        this._bus.unregisterMap(this._deregister);
    }
}
